// The tab model: which places the bar offers, and what each badge counts.
//
// One answer for the phone's bottom bar and the tablet's rail, so the two can
// never disagree about what the places are. Pure, because "what does the bar
// hold" is worth a test that needs no DOM.
//
// Frame v2.12: five places — Home · Pods · Quality · Andon · More. The
// andons have a tab of their own, and what used to be Alerts is Quality:
// same key, same /alerts path, a different name. This is the one bar every
// session gets. Whether the Andon tab's rows open this app's own thread or
// the read-only sheet is the andon preview's business, not the bar's.

import type { IconName } from '@/components/ui/Icon';
import type { StringKey } from '@/i18n/strings';
import type { Tab } from '@/store/slices/ui';

export interface TabSpec {
  key: Tab;
  icon: IconName;
  /** The label's translation key, so the bar and the rail read the same word. */
  label: StringKey;
}

/** The places, in the order they are drawn. */
export const TABS: readonly TabSpec[] = [
  { key: 'home', icon: 'home', label: 'homeT' },
  { key: 'pods', icon: 'pod', label: 'podsT' },
  // Same key, same /alerts path, a different name: the andons have gone to
  // their own tab, and what is left is quality alerts.
  { key: 'alerts', icon: 'checkCircle', label: 'qualityT' },
  { key: 'andon', icon: 'hazard', label: 'andonTabT' },
  { key: 'more', icon: 'more', label: 'moreT' },
];

/**
 * What each badge counts, the way frame v2.12 does (script L887-890):
 *
 *   Andon     the unanswered andons PLUS the starved work centres. A starved
 *             station is a line about to stop for want of a pod, which is an
 *             andon in everything but the raising, and the frame lists it on
 *             the Andon tab under the andons (L939) — so it is counted there.
 *   Quality   the quality alerts alone: the blocking ones for a leader, the
 *             operator's own for an operator. To-dos are not counted; they
 *             live on More, under My work, and a badge on a tab that does
 *             not list them would send somebody to look for a row that is
 *             not there.
 *
 * The two do not add up to the one badge the old Alerts tab wore,
 * deliberately: that badge counted to-dos, and the Quality tab no longer
 * lists them.
 */
export function tabBadges(counts: { andons: number; starved: number; quality: number }): Partial<Record<Tab, number>> {
  const at0 = (n: number) => Math.max(n, 0);
  return { alerts: at0(counts.quality), andon: at0(counts.andons) + at0(counts.starved) };
}
